export type QuestCodeResult =
  | { kind: 'error'; message: string }
  | { kind: 'success'; redirectTo: string };

export type QuestPageId = 'coeur' | 'lampe' | 'voiture11' | 'b6' | 'b6bis' | 'bouton' | 'boutonbis' | 'fin' | 'disparition' | 'disparitionbis' | 'disparitionter';

export type QuestIntroTextAlign = 'center' | 'justify';

export type QuestPageDefinition = {
  id: QuestPageId;
  path: string;
  showHeader: boolean;
  showFooter: boolean;
  showModal: boolean;
  panelRevealMs: number;
  /** Texte d’intro ; séparer les paragraphes avec `\n\n` ou une ligne vide. */
  introText: string | null;
  /** Alignement du texte d’intro (défaut : `center`). */
  introTextAlign?: QuestIntroTextAlign;
  modal: {
    prompt: string;
    inputLabel: string;
    inputId: string;
    submitLabel: string;
  } | null;
  /** Code unique accepté (comportement par défaut). */
  acceptedCode?: string;
  /** Route interne de redirection si le code est bon (comportement par défaut). */
  successRedirect?: string;
  /** Déclenche l'effet glitch avant la redirection. */
  glitchBeforeRedirect?: boolean;
  /** Surcharge pour une logique plus complexe. */
  handleCode?: (code: string) => QuestCodeResult;
};

export type QuestPageConfig = QuestPageDefinition & {
  introTextAlign: QuestIntroTextAlign;
  handleCode: (code: string) => QuestCodeResult;
};

const DEFAULT_CODE_ERROR = 'Ce code ne me dit rien…';

function normalizeQuestCodeForComparison(code: string): string {
  return code
    .trim()
    .replace(/['\u2019]/g, '')
    .replace(/\s+/g, '')
    .toLocaleLowerCase('fr-FR');
}

/** Compare la saisie au code attendu (sans espaces, insensible à la casse). */
export function matchesQuestCode(input: string, acceptedCode: string): boolean {
  return (
    normalizeQuestCodeForComparison(input) === normalizeQuestCodeForComparison(acceptedCode)
  );
}

function createDefaultCodeHandler(
  acceptedCode: string,
  successRedirect: string
): (code: string) => QuestCodeResult {
  return (code: string) => {
    if (matchesQuestCode(code, acceptedCode)) {
      return { kind: 'success', redirectTo: successRedirect };
    }
    return { kind: 'error', message: DEFAULT_CODE_ERROR };
  };
}

function resolveQuestPage(page: QuestPageDefinition): QuestPageConfig {
  const base = {
    ...page,
    introTextAlign: page.introTextAlign ?? 'center',
  };

  if (page.showModal && page.modal) {
    if (!page.acceptedCode || !page.successRedirect) {
      throw new Error(
        `La page quête « ${page.id} » doit définir acceptedCode et successRedirect, ou fournir handleCode.`
      );
    }

    return {
      ...base,
      handleCode:
        page.handleCode ?? createDefaultCodeHandler(page.acceptedCode, page.successRedirect),
    };
  }

  return {
    ...base,
    handleCode: page.handleCode ?? (() => ({ kind: 'error', message: DEFAULT_CODE_ERROR })),
  };
}

const sharedCodeModal = {
  showModal: true,
  panelRevealMs: 1000,
  modal: {
    prompt: 'Avez-vous un code pour moi ?',
    inputLabel: 'Code',
    inputId: 'quest-code',
    submitLabel: 'Valider',
  },
} as const;

/** Déclaration centralisée des pages /quest/* (textes, options d’affichage, codes). */
const QUEST_PAGE_DEFINITIONS: QuestPageDefinition[] = [
  {
    id: 'lampe',
    path: '/quest/lampe',
    showHeader: false,
    showFooter: false,
    introText: 'Merci, j’espérais que vous trouviez ce message ! Il y a certaines informations que j’hésitais à révéler, mais je crois que vous méritez d’en savoir un peu plus sur ce lieu.\n\nUn phénomène étrange m’a intrigué et j’ai sillonné la France entière pour essayer de comprendre ce qu’il se passait. Sans doute voudriez-vous prendre le train et suivre ma route ?\n\nSoyez vigilant.es... et si vous séchez, adressez-vous aux deux frères de la mariée ou aux témoins, expliquez-leur votre problème et ils pourront peut-être vous donner des indices. Si leurs indications ne sont pas suffisantes, n’hésitez pas à demander aux mariés !',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    showModal: false,
  },
  {
    id: 'voiture11',
    path: '/quest/voiture11',
    showHeader: false,
    showFooter: false,
    introText: 'Félicitations, vous avez retracé mon chemin et retrouvé la bonne voiture !\n\nPlus je voyageais et plus il me semblait évident que les particularités que j’avais identifiées n’apparaissaient qu’ici. Lorsque j’en parlais autour de moi, personne ne me croyait. Je suis donc passé par la gare d’Austerlitz et ai noté un rendez-vous codé dans mon carnet, déposé sur le meuble au centre de la terrasse.',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    showModal: false,
  },
  {
    id: 'b6',
    path: '/quest/b6',
    showHeader: false,
    showFooter: false,
    introText: 'Je ne peux pas partager ce secret avec tout le monde, mais au fil des années j’ai remarqué que parfois, dans cette gare précisément, les arrivées des trains ne correspondaient pas aux horaires attendus. Cette irrégularité était surprenante.\n\nLorsque j’ai étudié les perturbations des trains, j’ai trouvé une similarité. Certains étaient en retard, d’autres en avance, et d’autres n’arrivaient même jamais ! Il y avait comme un motif qui se dessinait au fur et à mesure que je faisais des calculs pour comprendre ces irrégularités. J’ai interrogé mes collègues mais ils ne savaient pas de quoi je parlais. J’ai peu à peu développé un code pour laisser des traces de mes théories (15 983).',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    acceptedCode: 'V2 F83',
    successRedirect: '/quest/b6bis',
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-b6',
      prompt: 'L’avez-vous déchiffré ?',
      submitLabel: 'Valider',
    },
  },
  {
    id: 'b6bis',
    path: '/quest/b6bis',
    showHeader: false,
    showFooter: false,
    introText: 'Dans la nuit du 2 au 3 août 1886, le tunnel de Montrouge s’est effondré sur une vingtaine de mètres. Le trafic a repris entre Glacière-Gentilly et Montrouge le 7 avril 1887.\n\nLes autorités ont imputé cet accident à l’effet du gaz et des fumées des locomotives sur les maçonneries, mais j’ai compris ce qui était réellement à l’origine de l’éboulement : cette gare était soumise à des distorsions spatio-temporelles. Cela expliquait aussi le décalage des trains !\n\nReprenez pour quelques temps le parcours principal. Vous verrez que personne n’évoque ma théorie, évidemment, mais j’ai inséré à la prochaine étape un indice pour vous. Lorsque vous croiserez de nouveau l’indice, suivez-le !',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    showModal: false,
  },
  {
    id: 'bouton',
    path: '/quest/bouton',
    showHeader: false,
    showFooter: false,
    introText: null,
    ...sharedCodeModal,
    acceptedCode: 'Je dus courir après l\'ombre du temps',
    successRedirect: '/quest/boutonbis',
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-bouton',
      prompt: '22 0 / 14 20 0 / 14 20 25 0 0 0 / 0 20 17 17 25 / 0’14 21 10 13 11 / 0 0 / 0 13 12 7 5',
      submitLabel: 'Valider',
    },
  },
  {
    id: 'boutonbis',
    path: '/quest/boutonbis',
    showHeader: false,
    showFooter: false,
    introText: 'Ce message est extrêmement important. Tout le monde a toujours cru que mes théories ne reposaient que sur des suppositions, mais ma propre vie en est la preuve : cette gare permet de voyager dans le temps !\n\nEn effet, il me suffit de pousser cette porte (ce qui vous est interdit aujourd’hui) et d’entrer dans cette pièce à une heure précise pour pouvoir quitter une époque et en découvrir une autre ! C’est ce que j’ai commencé à faire et j’ai ainsi vécu de l’intérieur l’histoire de ce lieu merveilleux.\n\nRejoignez le parcours principal en haut du grand escalier et suivez comme les autres les évolutions dont j’ai été témoin.',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    showModal: false,
  },
  {
    id: 'coeur',
    path: '/quest/coeur',
    showHeader: true,
    showFooter: true,
    introText: 'Quelque chose vous a conduit jusqu’ici : peut-être un indice, peut-être un hasard…',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    acceptedCode: 'GHW',
    successRedirect: '/quest/fin',
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-coeur',
      prompt: 'Avez-vous un code pour moi ?',
      submitLabel: 'Valider',
    },
  },
  {
    id: 'fin',
    path: '/quest/fin',
    showHeader: false,
    showFooter: false,
    introText: 'Un dernier mot : j’ai été ravi de pouvoir vous faire part de mes théories. J’espère vous avoir convaincu. Maintenant, gardez le secret !\n\nJ’ai échangé pour la première fois avec Jean-Christophe alors qu’il était adolescent et parcourait les rayonnages du gigantesque magasin d’informatique qui se tenait ici, à la recherche d’une carte accélératrice 3DFX Voodoo 2. C’est beaucoup plus tard, et de l’autre côté, que j’ai rencontré Virginie. Il y a quelques années, elle était en visite sur les voies avec Jean-Christophe pour découvrir les prochaines pépites du street art parisien.\n\nJ’ai fait leur connaissance à deux périodes différentes de leur vie, mais nous nous sommes lié d’amitié et je suis ravi de les voir aussi heureux ensemble. Ils ont tous les deux compris mes théories et c’est aujourd’hui avec la plus grande joie que je suis présent à leur mariage !',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    showModal: false,
  },
  // GE related messages
  {
    id: 'disparition',
    path: '/quest/disparition',
    showHeader: false,
    showFooter: false,
    introText: 'J’espérais que tu me suivrais et que tu aurais l’occasion de lire ce message. Comme tu l’as sans doute déjà compris, quelque chose de grave s’est passé. Au moment où tu lis ces mots, l’aventure que tu t’apprêtes à vivre a, d’une certaine manière, déjà eu lieu. Je ne peux cependant pas t’en révéler davantage, au risque de perturber l’ordre du temps.\n\nMais avant de te permettre d’aller plus loin, je dois m’assurer que tu es bien la personne que je cherche.',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    acceptedCode: 'chef de gare',
    successRedirect: '/quest/disparitionbis',
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-disparition',
      prompt: 'Qui suis-je ?',
      submitLabel: 'Valider',
    },
  },
  {
    id: 'disparitionbis',
    path: '/quest/disparitionbis',
    showHeader: false,
    showFooter: false,
    introText: 'Tu sais, en effet, qui je suis.\n\nJe sais que tu n’auras donc aucun mal à résoudre cette énigme : « Je demeure en un lieu où l’ordre du temps se trouve renversé : le présent y succède au passé, tandis que le futur précède ce même passé. Quel est donc ce lieu où les âges se jouent de leur cours naturel ? »',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    acceptedCode: 'un dictionnaire',
    successRedirect: '/quest/disparitionter',
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-disparition',
      prompt: 'Quelle est ta réponse ?',
      submitLabel: 'Valider',
    },
  },
  {
    id: 'disparitionter',
    path: '/quest/disparitionter',
    showHeader: false,
    showFooter: false,
    introText: 'C’est bien toi. Je peux donc t’en dire davantage…\n\nJe vais te permettre de découvrir quelque chose que personne d’autre n’a encore eu la chance de voir. Tu vas pouvoir entrer dans cette pièce qui était fermée le 18 juillet.\n\nUn ami très cher a disparu sous tes yeux. J’ai eu… j’ai besoin de toi pour le retrouver. Sa disparition est intimement liée au cours du temps. Les événements du passé doivent avoir lieu tels qu’ils ont eu lieu, que tu t’en sois aperçue ou non. Le moindre changement pourrait tout bouleverser.\n\nRappelle-toi bien de ces mots :\n\n« Porte fermée. Le matin, le futur. L’après-midi, le passé. Cinq minutes représentent une année… »\n\nIl ne me reste qu’une dernière chose à vérifier avant de te laisser partir à sa recherche.',
    introTextAlign: 'justify',
    ...sharedCodeModal,
    acceptedCode: 'gustave eiffel',
    successRedirect: 'http://home.rona.fr:5173',
    glitchBeforeRedirect: true,
    modal: {
      ...sharedCodeModal.modal,
      inputId: 'code-disparition',
      prompt: 'Quel est le nom de cet ami ?',
      submitLabel: 'Valider',
    },
  },
];

export const QUEST_PAGES: QuestPageConfig[] = QUEST_PAGE_DEFINITIONS.map(resolveQuestPage);

export function getQuestPageConfig(pathname: string): QuestPageConfig | undefined {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  return QUEST_PAGES.find((entry) => entry.path === normalizedPath);
}

export function splitQuestIntroParagraphs(introText: string): string[] {
  return introText
    .split(/(?:\n\n)+/i)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function isQuestPath(pathname: string): boolean {
  return pathname.startsWith('/quest');
}
